<script setup>
import { computed, ref, watch, reactive } from "vue";
import ViewportImage from "~/components/viewport/ViewportImage.vue";

const props = defineProps({
  urls: {
    type: Array,
    default: () => [],
  },
  alt: {
    type: String,
    default: "AI Generated Image",
  },
  activeIndex: {
    type: Number,
    default: undefined,
  },
  defaultActiveIndex: {
    type: Number,
    default: 0,
  },
  layout: {
    type: String,
    default: undefined,
  },
  defaultLayout: {
    type: String,
    default: "carousel",
  },
  showLayoutToggle: {
    type: Boolean,
    default: true,
  },
  showIndexControls: {
    type: Boolean,
    default: true,
  },
  controlsPlacement: {
    type: String,
    default: "bottom",
  },
  enablePreview: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["update:activeIndex", "update:layout", "preview"]);

const { openImagePreview } = useImagePreview();

const normalizedUrls = computed(() => {
  const raw = Array.isArray(props.urls) ? props.urls : [];
  return raw.filter((url) => typeof url === "string" && url.trim().length > 0);
});

const internalActiveIndex = ref(props.defaultActiveIndex);
const internalLayout = ref(props.defaultLayout);
const renderedIndexMap = reactive({});

const currentActiveIndex = computed(() => {
  return typeof props.activeIndex === "number"
    ? props.activeIndex
    : internalActiveIndex.value;
});

const currentLayout = computed(() => {
  return typeof props.layout === "string" ? props.layout : internalLayout.value;
});

watch(
  () => normalizedUrls.value.length,
  (len) => {
    if (len === 0) return;
    const idx = currentActiveIndex.value;
    if (idx < 0 || idx >= len) setActiveIndex(0);
  },
  { immediate: true }
);

watch(
  normalizedUrls,
  () => {
    Object.keys(renderedIndexMap).forEach((key) => delete renderedIndexMap[key]);
    renderedIndexMap[currentActiveIndex.value] = true;
  },
  { immediate: true }
);

watch(
  currentActiveIndex,
  (idx) => {
    renderedIndexMap[idx] = true;
  },
  { immediate: true }
);

function setActiveIndex(index) {
  const len = normalizedUrls.value.length;
  if (len === 0) return;
  const next = Math.max(0, Math.min(index, len - 1));
  renderedIndexMap[next] = true;
  if (typeof props.activeIndex !== "number") internalActiveIndex.value = next;
  emit("update:activeIndex", next);
}

function setLayout(layout) {
  const next = layout === "grid" ? "grid" : "carousel";
  if (typeof props.layout !== "string") internalLayout.value = next;
  emit("update:layout", next);
}

function toggleLayout() {
  setLayout(currentLayout.value === "grid" ? "carousel" : "grid");
}

function handlePreview(url) {
  if (!url) return;
  emit("preview", url);
  if (props.enablePreview) openImagePreview(url);
}

const controlsPlacementClass = computed(() => {
  return props.controlsPlacement === "top" ? "top-2" : "bottom-2";
});

const layoutToggleIcon = computed(() => {
  return currentLayout.value === "grid"
    ? "i-lucide-gallery-horizontal-end"
    : "i-lucide-layout-grid";
});

const layoutToggleTitle = computed(() => {
  return currentLayout.value === "grid" ? "Carousel view" : "Grid view";
});

const indexControlsPaddingClass = computed(() => {
  return props.controlsPlacement === "top" ? "pr-10" : "";
});
</script>

<template>
  <div class="relative w-full h-full">
    <div
      v-if="normalizedUrls.length === 0"
      class="w-full h-full flex items-center justify-center"
    >
      <slot name="empty">
        <p class="text-gray-500 text-sm">No image available</p>
      </slot>
    </div>

    <template v-else-if="normalizedUrls.length === 1">
      <ViewportImage
        :src="normalizedUrls[0]"
        :alt="alt"
        img-class="object-contain w-full h-full cursor-pointer"
        @click="handlePreview"
      />
    </template>

    <template v-else>
      <button
        v-if="showLayoutToggle"
        type="button"
        class="absolute top-2 right-2 z-40 rounded-lg bg-black/50 text-white border border-white/10 px-2 py-1 hover:bg-black/70 transition pointer-events-auto"
        @click.stop="toggleLayout"
        :title="layoutToggleTitle"
      >
        <UIcon :name="layoutToggleIcon" class="w-4 h-4" />
      </button>

      <!-- Grid layout -->
      <div v-if="currentLayout === 'grid'" class="w-full h-full relative">
        <div
          class="w-full h-full grid grid-cols-2 auto-rows-[minmax(120px,1fr)] gap-1 p-1 overflow-auto custom-scroll"
        >
          <div
            v-for="(url, index) in normalizedUrls"
            :key="`output-img-${index}-${url}`"
            class="relative overflow-hidden rounded-lg bg-black/10"
          >
            <ViewportImage
              :src="url"
              :alt="alt"
              img-class="object-contain w-full h-full cursor-pointer"
              @click="handlePreview"
            />
            <div
              class="absolute top-1 left-1 z-10 rounded-md bg-black/60 text-white text-[10px] px-1.5 py-0.5"
            >
              {{ index + 1 }}
            </div>
          </div>
        </div>
      </div>

      <!-- Carousel layout -->
      <div v-else class="w-full h-full">
        <div class="w-full h-full relative">
          <div
            v-for="(url, index) in normalizedUrls"
            :key="`output-carousel-${index}-${url}`"
            class="absolute inset-0"
            v-show="index === currentActiveIndex"
          >
            <ViewportImage
              v-if="renderedIndexMap[index]"
              :src="url"
              :alt="alt"
              img-class="object-contain w-full h-full cursor-pointer"
              @click="handlePreview"
            />
          </div>
        </div>

        <div
          v-if="showIndexControls"
          class="absolute left-2 right-2 z-30 flex items-center justify-center gap-2 pointer-events-auto"
          :class="[controlsPlacementClass, indexControlsPaddingClass]"
        >
          <div
            class="flex items-center justify-center gap-1 overflow-x-auto custom-scroll w-full"
          >
            <button
              v-for="(_, index) in normalizedUrls"
              :key="`output-index-${index}`"
              type="button"
              class="h-7 min-w-7 px-2 rounded-md border text-xs font-medium transition"
              :class="
                index === currentActiveIndex
                  ? 'bg-[#6E28FD] text-white border-[#6E28FD]'
                  : 'bg-black/40 text-gray-200 border-white/10 hover:bg-black/60'
              "
              @click.stop="setActiveIndex(index)"
            >
              {{ index + 1 }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
